import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, CalendarClock, HeartPulse, Plus, RotateCcw, Utensils, X, type LucideIcon } from 'lucide-react';
import {
  DEFAULT_IPD_DOSE_TIMES,
  DEFAULT_IPD_MEAL_TIMES,
  IPD_MEAL_LABELS,
  IPD_OVERDUE_ESCALATION_CHOICES,
  IPD_TIMED_FREQUENCIES,
  resolveIpdOverdueEscalationMinutes,
  resolveIpdScheduleSettings,
  type IpdScheduleSettingsInput,
  type IpdTimedFrequency,
} from '@his/shared';
import { get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { formatTimeEntry } from '@/lib/date-entry';
import { Button, Card, CardContent, CardHeader, CardTitle, TimePicker } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import {
  DOSE_TIMES_PER_DAY,
  MEAL_TIMES_MAX,
  VITALS_INTERVAL_CHOICES,
  doseTimesPatch,
  doseTimesProblem,
  intervalParts,
  isDefaultTimes,
  sortTimes,
  withTime,
  withoutTime,
  wardIntervalsPatch,
} from './ipd-schedule-settings';

interface WardRow {
  id: string;
  code: string;
  name: string;
}

const DEFAULT_CHOICE = 'default';

function intervalLabel(minutes: number): string {
  const parts = intervalParts(minutes);
  if (parts.kind === 'off') return tr('ipd', 'io_set_off');
  if (parts.kind === 'hours') return tr('ipd', 'io_set_hours', { hours: parts.hours });
  return tr('ipd', 'io_set_minutes', { minutes: parts.minutes });
}

function intervalOptions(current: number): { value: string; label: string }[] {
  const values: number[] = [...VITALS_INTERVAL_CHOICES];
  if (!values.includes(current)) values.push(current);
  return values.sort((a, b) => a - b).map((minutes) => ({ value: String(minutes), label: intervalLabel(minutes) }));
}

function Block({ icon: Icon, title, hint, children }: { icon: LucideIcon; title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="space-y-3 border-t pt-4 first:border-t-0 first:pt-0">
      <header className="flex items-start gap-2.5">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-3.5 w-3.5" aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">{title}</h3>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

function TimeChips({
  label,
  times,
  max,
  disabled,
  chipLabel,
  onChange,
}: {
  label: string;
  times: readonly string[];
  max: number;
  disabled: boolean;
  chipLabel?: (index: number) => string | null;
  onChange: (next: string[]) => void;
}) {
  const [adding, setAdding] = useState('');
  const full = times.length >= max;
  const taken = adding !== '' && times.includes(adding);
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={label}>
      {times.map((time, index) => {
        const extra = chipLabel?.(index);
        return (
          <span key={time} className="inline-flex items-center gap-1 rounded-full border bg-muted/50 py-0.5 pe-1 ps-2.5 text-xs font-medium tabular dark:bg-muted/30">
            {formatTimeEntry(time, 24)}
            {extra && <span className="font-normal text-muted-foreground">{extra}</span>}
            <button
              type="button"
              className="focus-ring grid h-5 w-5 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
              aria-label={tr('ipd', 'io_set_remove_time', { time: formatTimeEntry(time, 24) })}
              disabled={disabled || times.length <= 1}
              onClick={() => onChange(withoutTime(times, time))}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        );
      })}
      {!full && !disabled && (
        <span className="inline-flex items-center gap-1">
          <TimePicker className="w-28" value={adding} onChange={setAdding} step={15} hourCycle={24} aria-label={tr('ipd', 'io_set_add_time')} />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={adding === '' || taken}
            onClick={() => {
              onChange(withTime(times, adding, max));
              setAdding('');
            }}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> {tr('ipd', 'io_set_add_time')}
          </Button>
          {taken && <span className="text-xs text-destructive">{tr('ipd', 'io_set_time_taken')}</span>}
        </span>
      )}
    </div>
  );
}

function DoseTimesRow({
  code,
  saved,
  disabled,
  onSave,
}: {
  code: IpdTimedFrequency;
  saved: readonly string[];
  disabled: boolean;
  onSave: (times: string[]) => void;
}) {
  const [draft, setDraft] = useState<string[] | null>(null);
  const times = draft ?? sortTimes(saved);
  const problem = doseTimesProblem(code, times);
  const change = (next: string[]) => {
    if (doseTimesProblem(code, next)) {
      setDraft(next);
      return;
    }
    setDraft(null);
    onSave(next);
  };
  const atDefault = isDefaultTimes(times, DEFAULT_IPD_DOSE_TIMES[code]);
  return (
    <div className="grid gap-2 rounded-lg border p-3 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-start">
      <div className="min-w-0">
        <p className="text-sm font-medium">{tr('ipd', `io_set_freq_${code}`)}</p>
        <p className={cn('text-xs', problem ? 'text-warning' : 'text-muted-foreground')}>
          {problem ? tr('ipd', 'io_set_needs_times', { count: problem.expected, actual: problem.actual }) : tr('ipd', 'io_set_times_a_day', { count: DOSE_TIMES_PER_DAY[code] })}
        </p>
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <TimeChips label={tr('ipd', `io_set_freq_${code}`)} times={times} max={DOSE_TIMES_PER_DAY[code]} disabled={disabled} onChange={change} />
        {!atDefault && !disabled && (
          <Button type="button" size="sm" variant="ghost" onClick={() => change([...DEFAULT_IPD_DOSE_TIMES[code]])}>
            <RotateCcw className="h-3.5 w-3.5" aria-hidden /> {tr('ipd', 'io_set_reset')}
          </Button>
        )}
      </div>
    </div>
  );
}

export function IpdSchedulesCard({ readOnly }: { readOnly: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const settings = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => get<{ settings: Record<string, unknown> }>('/org/settings'),
  });
  const wards = useQuery({
    queryKey: ['ipd-wards', 'settings'],
    queryFn: () => get<WardRow[]>('/ipd/wards?limit=200'),
    enabled: can('bed.read'),
    staleTime: 5 * 60_000,
  });
  const raw = settings.data?.settings;
  const current = resolveIpdScheduleSettings(raw);
  const overdue = resolveIpdOverdueEscalationMinutes(raw);
  const disabled = readOnly || !settings.data;

  const save = useMutation({
    mutationFn: (patch: IpdScheduleSettingsInput) => put('/org/settings', patch),
    onSuccess: () => {
      toast.success(tr('ipd', 'io_set_saved'));
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['ipd-schedule-settings'] });
    },
    onError: (error) => toast.error(tr('ipd', 'io_set_could_not_save'), describeError(error)),
  });
  const busy = disabled || save.isPending;

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm">{tr('ipd', 'io_set_title')}</CardTitle>
        <p className="text-xs text-muted-foreground">{tr('ipd', 'io_set_desc')}</p>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <Block icon={CalendarClock} title={tr('ipd', 'io_set_dose_times')} hint={tr('ipd', 'io_set_dose_times_hint')}>
          <div className="space-y-2">
            {IPD_TIMED_FREQUENCIES.map((code) => (
              <DoseTimesRow
                key={`${code}:${current.doseTimes[code].join(',')}`}
                code={code}
                saved={current.doseTimes[code]}
                disabled={busy}
                onSave={(times) => save.mutate({ ipdDoseTimes: doseTimesPatch(current.doseTimes, code, times) })}
              />
            ))}
          </div>
        </Block>

        <Block icon={Utensils} title={tr('ipd', 'io_set_meal_times')} hint={tr('ipd', 'io_set_meal_times_hint')}>
          <div className="flex flex-wrap items-center gap-2">
            <TimeChips
              label={tr('ipd', 'io_set_meal_times')}
              times={sortTimes(current.mealTimes)}
              max={MEAL_TIMES_MAX}
              disabled={busy}
              chipLabel={(index) => {
                const meal = IPD_MEAL_LABELS[index];
                return meal ? tr('ipd', `io_set_meal_${meal}`) : null;
              }}
              onChange={(times) => save.mutate({ ipdMealTimes: times })}
            />
            {!isDefaultTimes(current.mealTimes, DEFAULT_IPD_MEAL_TIMES) && !busy && (
              <Button type="button" size="sm" variant="ghost" onClick={() => save.mutate({ ipdMealTimes: [...DEFAULT_IPD_MEAL_TIMES] })}>
                <RotateCcw className="h-3.5 w-3.5" aria-hidden /> {tr('ipd', 'io_set_reset')}
              </Button>
            )}
          </div>
        </Block>

        <Block icon={HeartPulse} title={tr('ipd', 'io_set_vitals')} hint={tr('ipd', 'io_set_vitals_hint')}>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">{tr('ipd', 'io_set_vitals_hospital')}</p>
              <ChoiceButtons
                inline
                label={tr('ipd', 'io_set_vitals_hospital')}
                value={String(current.vitalsIntervalMinutes)}
                options={intervalOptions(current.vitalsIntervalMinutes)}
                disabled={busy}
                onChange={(value) => save.mutate({ ipdVitalsIntervalMinutes: Number(value) })}
              />
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">{tr('ipd', 'io_set_wards')}</p>
              {!can('bed.read') || wards.isError ? (
                <p className="text-xs text-muted-foreground">{tr('ipd', 'io_set_wards_unavailable')}</p>
              ) : (wards.data ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">{wards.isLoading ? '…' : tr('ipd', 'io_set_no_wards')}</p>
              ) : (
                <ul className="space-y-2">
                  {(wards.data ?? []).map((ward) => {
                    const own = current.wardVitalsIntervalMinutes[ward.id];
                    return (
                      <li key={ward.id} className="grid gap-1.5 rounded-lg border p-2.5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:items-center">
                        <span className="min-w-0 truncate text-sm font-medium">
                          <span className="font-mono text-xs text-muted-foreground">{ward.code}</span> {ward.name}
                        </span>
                        <ChoiceButtons
                          inline
                          size="sm"
                          label={ward.name}
                          value={own === undefined ? DEFAULT_CHOICE : String(own)}
                          options={[{ value: DEFAULT_CHOICE, label: tr('ipd', 'io_set_ward_default') }, ...intervalOptions(own ?? current.vitalsIntervalMinutes)]}
                          disabled={busy}
                          onChange={(value) =>
                            save.mutate({
                              ipdWardVitalsIntervalMinutes: wardIntervalsPatch(current.wardVitalsIntervalMinutes, ward.id, value === DEFAULT_CHOICE ? null : Number(value)),
                            })
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </Block>

        <Block icon={AlarmClock} title={tr('ipd', 'io_set_overdue')} hint={tr('ipd', 'io_set_overdue_hint')}>
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">{tr('ipd', 'io_set_overdue_label')}</p>
            <ChoiceButtons<string>
              className="sm:max-w-md"
              label={tr('ipd', 'io_set_overdue_label')}
              value={(IPD_OVERDUE_ESCALATION_CHOICES as readonly number[]).includes(overdue) ? String(overdue) : ''}
              disabled={busy}
              options={IPD_OVERDUE_ESCALATION_CHOICES.map((minutes) => ({ value: String(minutes), label: intervalLabel(minutes), tone: minutes === 0 ? 'warning' : 'default' }))}
              onChange={(value) => save.mutate({ ipdOverdueEscalationMinutes: Number(value) })}
            />
            {!(IPD_OVERDUE_ESCALATION_CHOICES as readonly number[]).includes(overdue) && <p className="text-xs text-muted-foreground">{intervalLabel(overdue)}</p>}
          </div>
        </Block>
      </CardContent>
    </Card>
  );
}
